import { SettingsContextProvider } from "@/components/common/settings-context/settings-context";
import { useHandleTokenAuthError } from "@/hooks/use-handle-token-auth-error";
import { useInitAppAnalytics } from "@/hooks/use-init-app-analytics";
import { EnsureProjectAccess } from "@/permissions/ensure-project-access";
import { runtimeConfig } from "@/runtime-config";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { APP_VERSION } from "@/utils/env-constants";
import { assert } from "@faro-lotv/foundation";
import { selectProjectName } from "@faro-lotv/project-source";
import { ApiClientContextProvider } from "@faro-lotv/service-wires";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type PropsWithChildren,
} from "react";
import { useParams } from "react-router-dom";

/**
 * @returns Provides the necessary context providers to check project access.
 * See also `ProjectProvider` and its usage.
 * Note: The allowProjectViewing state flag in `HBEnsureProjectAccess` only works correctly if the
 *       component only appears once in the component tree. Therefore, `ProjectAccessProvider` is
 *       set up once as a route-scoped provider in App.tsx, which comes with the additional benefit
 *       of loading the data only once.
 */
export function ProjectAccessProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { projectId } = useParams();
  assert(projectId, "No project ID provided");
  const currentUser = useAppSelector(selectCurrentUser);
  const projectName = useAppSelector(selectProjectName);
  const handleTokenAuthError = useHandleTokenAuthError();
  // Consider the project name as private data for Amplitude tracking.
  // This is only a precautionary measure since none of the currently existing Analytics.track
  // calls use projectName in the property object.
  const privateData = useMemo(() => ({ projectName }), [projectName]);
  useInitAppAnalytics(privateData);

  const [clientName, setClientName] = useState("sphere-web-viewer");
  const clientId = useMemo(() => `${clientName}/${APP_VERSION}`, [clientName]);
  const clientIdContext = useMemo(
    () => ({ clientId, setClientName }),
    [clientId, setClientName],
  );

  return (
    <ClientIdContext value={clientIdContext}>
      <ApiClientContextProvider
        projectId={projectId}
        userId={currentUser?.id}
        clientId={clientId}
        apiBaseUrls={runtimeConfig.backendEndpoints}
        onTokenAuthError={handleTokenAuthError}
      >
        <SettingsContextProvider>
          <EnsureProjectAccess projectId={projectId}>
            {children}
          </EnsureProjectAccess>
        </SettingsContextProvider>
      </ApiClientContextProvider>
    </ClientIdContext>
  );
}

type ClientIdContextData = {
  /** Change the name used to identify the client/app when sending API requests. */
  setClientName(name: string): void;
  /** The currently used client ID in the format client/version. */
  clientId: string;
};

/** Context to access and adjust the client ID used for the API clients. */
const ClientIdContext = createContext<ClientIdContextData | undefined>(
  undefined,
);

/**
 * @returns The context for managing the active client ID.
 */
export function useClientId(): ClientIdContextData {
  const ctx = useContext(ClientIdContext);
  assert(ctx, "useClientId should be used only inside a ProjectAccessProvider");
  return ctx;
}

type ClientNameProps = {
  /** Name to identify the client/app when sending API requests. */
  clientName: string;

  /** The element to render. */
  children: JSX.Element;
};

/**
 * Wrapper that sets the API client name while its children are rendered.
 *
 * Use this inside a `<Route>`'s `element` prop. We can't wrap the `<Route>` itself,
 * because React Router's `<Routes>` requires its direct children to be literal `<Route>`
 * elements (or fragments) and rejects custom wrappers around `<Route>`.
 *
 * @returns the children, after registering the given client name.
 */
export function WithClientName({
  clientName,
  children,
}: ClientNameProps): JSX.Element {
  const { setClientName } = useClientId();

  useEffect(() => {
    setClientName(clientName);
  }, [clientName, setClientName]);

  return children;
}
